import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useFilters } from '../../contexts/FiltersContext';
import { propertyService } from '../../services/propertyService';
import { Property } from '../../types/property';
import { AnalysisAccordion } from './AnalysisAccordion';
import { MetricsCalculator } from '../../utils/metricsCalculator';

const PropertyList: React.FC = () => {
  const [baseProperties, setBaseProperties] = useState<Property[]>([]);  // Original properties from DB
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndexes, setCurrentPhotoIndexes] = useState<Record<number, number>>({});
  const [sortBy, setSortBy] = useState<string>('cash_flow_desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const PAGE_SIZE = 30;
  const [purchasePriceInputs, setPurchasePriceInputs] = useState<Record<number, string>>({});
  const [renovationsInputs, setRenovationsInputs] = useState<Record<number, string>>({});  // User-input renovations
  const [priceRecalculating, setPriceRecalculating] = useState<Record<number, boolean>>({});
  const [expandedSectionsMap, setExpandedSectionsMap] = useState<Record<number, string[]>>({});
  // ZIP search state
  const [zipInput, setZipInput] = useState<string>('');
  const [activeZip, setActiveZip] = useState<string>('');
  const [zipError, setZipError] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);  // Total properties from API
  const [latestUpdate, setLatestUpdate] = useState<string | null>(null);  // Last property update timestamp
  const [lastSyncRun, setLastSyncRun] = useState<string | null>(null);  // When RapidAPI was last called
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);  // When API was last called
  const { filters, assumptions } = useFilters();
  const prevFiltersRef = useRef<string>('');

  // USA-wide bbox — returns whatever is currently in the DB, no API sync
  const USA_BBOX = { min_lon: -125.0, min_lat: 24.0, max_lon: -66.0, max_lat: 49.0 };

  const applySortOrder = (props: Property[], sort: string): number[] =>
    [...props].sort((a, b) => {
      switch (sort) {
        case 'cash_flow_desc': return (b.monthly_cash_flow || 0) - (a.monthly_cash_flow || 0);
        case 'cash_flow_asc':  return (a.monthly_cash_flow || 0) - (b.monthly_cash_flow || 0);
        case 'price_asc':      return (a.list_price || 0) - (b.list_price || 0);
        case 'price_desc':     return (b.list_price || 0) - (a.list_price || 0);
        case 'cap_rate_desc':  return (b.cap_rate || 0) - (a.cap_rate || 0);
        case 'cap_rate_asc':   return (a.cap_rate || 0) - (b.cap_rate || 0);
        default: return 0;
      }
    }).map(p => p.id);

  // Recalculated properties - derived from baseProperties + assumptions (instant updates!)
  const recalculatedProperties = useMemo(() => {
    if (baseProperties.length === 0) return [];
    const calculator = new MetricsCalculator(assumptions);
    const recalculated = calculator.recalculateProperties(baseProperties);
    return recalculated;
  }, [baseProperties, assumptions]);

  // Client-side filtering based on recalculated investment metrics
  const properties = useMemo(() => {
    let filtered = [...recalculatedProperties];

    // Apply investment minimum filters (based on recalculated values)
    if (filters.cash_flow_min !== undefined) {
      console.log('[PropertyList] Filtering by cash_flow_min:', filters.cash_flow_min);
      console.log('[PropertyList] Before filter:', filtered.length, 'properties');
      console.log('[PropertyList] First 5 cash flows:', filtered.slice(0, 5).map(p => ({
        id: p.id,
        address: p.address,
        monthly_cash_flow: p.monthly_cash_flow
      })));
      
      // Round both values to nearest dollar for comparison (since cards display as whole dollars)
      filtered = filtered.filter(p => Math.round(p.monthly_cash_flow || 0) >= Math.round(filters.cash_flow_min!));
      
      console.log('[PropertyList] After filter:', filtered.length, 'properties');
    }

    if (filters.cap_rate_min !== undefined) {
      filtered = filtered.filter(p => (p.cap_rate || 0) >= filters.cap_rate_min!);
    }

    if (filters.coc_return_min !== undefined) {
      filtered = filtered.filter(p => (p.cash_on_cash_return || 0) >= filters.coc_return_min!);
    }

    if (filters.roi_min !== undefined) {
      filtered = filtered.filter(p => (p.roi || 0) >= filters.roi_min!);
    }

    return filtered;
  }, [recalculatedProperties, filters.cash_flow_min, filters.cap_rate_min, filters.coc_return_min, filters.roi_min]);

  // Stable sort order derived from filtered properties + sortBy
  const sortedPropertyIds = useMemo(() => {
    return applySortOrder(properties, sortBy);
  }, [properties, sortBy]);

  const loadProperties = async (bbox: typeof USA_BBOX) => {
    try {
      setLoading(true);
      
      // Split filters: send only property-based filters to backend
      // Investment filters (cash_flow_min) are applied client-side after recalculation
      const backendFilters = {
        status: filters.status,
        property_type: filters.property_type,
        bedrooms_min: filters.bedrooms_min,
        baths_min: filters.baths_min,
        sqft_min: filters.sqft_min,
        sqft_max: filters.sqft_max,
        price_min: filters.price_min,
        price_max: filters.price_max,
      };
      
      // Fetch properties with ONLY property-based filters, no assumptions (use backend defaults)
      const data = await propertyService.getPropertiesInViewport(bbox, backendFilters, undefined);
      const props = data.features.map((f: any) => f.properties);
      setBaseProperties(props);  // Properties and sortedPropertyIds will auto-update via useMemo
      setTotalCount(data.total_count || props.length);  // Use exact count from API
      setLatestUpdate(data.latest_property_update || null);  // Store last update timestamp
      setLastSyncRun(data.last_sync_run || null);  // When RapidAPI was last called
      setLastFetchTime(new Date());  // Track when we fetched data
      setCurrentPage(1);
      setPurchasePriceInputs({});
      setRenovationsInputs({});
      setPriceRecalculating({});
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle ZIP code search submission
  const handleZipSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const zip = zipInput.trim();
    if (!/^\d{5}$/.test(zip)) {
      setZipError('Enter a valid 5-digit ZIP code');
      return;
    }
    setZipError('');
    setActiveZip(zip);

    // Resolve ZIP to bbox via Mapbox then load from DB
    const bbox = await propertyService.getZipBbox(zip);
    if (!bbox) {
      setZipError('Could not find that ZIP code');
      return;
    }

    try {
      // Split filters: send only property-based filters to backend
      const backendFilters = {
        status: filters.status,
        property_type: filters.property_type,
        bedrooms_min: filters.bedrooms_min,
        baths_min: filters.baths_min,
        sqft_min: filters.sqft_min,
        sqft_max: filters.sqft_max,
        price_min: filters.price_min,
        price_max: filters.price_max,
      };
      
      const data = await propertyService.getPropertiesInViewport(bbox, backendFilters, undefined);
      const props = data.features.map((f: any) => f.properties);
      setBaseProperties(props);  // Properties and sortedPropertyIds will auto-update via useMemo
      setTotalCount(data.total_count || props.length);  // Use exact count from API
      setLatestUpdate(data.latest_property_update || null);  // Store last update timestamp
      setLastSyncRun(data.last_sync_run || null);  // When RapidAPI was last called
      setLastFetchTime(new Date());  // Track when we fetched data
      setCurrentPage(1);
      setPurchasePriceInputs({});
      setRenovationsInputs({});
      setPriceRecalculating({});
    } catch {
      // Auth expired or network error
    }
  };

  const clearZipSearch = () => {
    setZipInput('');
    setActiveZip('');
    setZipError('');
    loadProperties(USA_BBOX);
  };

  // Reload from DB when property-based filters change
  // Assumptions and investment filters trigger automatic recalc/refilter via useMemo (instant!)
  useEffect(() => {
    // Only track property-based filters that require backend reload
    const backendFiltersString = JSON.stringify({
      status: filters.status,
      property_type: filters.property_type,
      bedrooms_min: filters.bedrooms_min,
      baths_min: filters.baths_min,
      sqft_min: filters.sqft_min,
      sqft_max: filters.sqft_max,
      price_min: filters.price_min,
      price_max: filters.price_max,
    });
    
    const isInitialMount = prevFiltersRef.current === '';
    const filtersChanged = prevFiltersRef.current !== backendFiltersString;

    // Update ref
    prevFiltersRef.current = backendFiltersString;

    // Only reload from backend if property-based filters changed
    // (cash_flow_min changes are handled client-side via useMemo)
    if (isInitialMount || filtersChanged) {
      if (activeZip) {
        propertyService.getZipBbox(activeZip).then(bbox => {
          if (bbox) loadProperties(bbox);
        });
      } else {
        loadProperties(USA_BBOX);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeZip]);

  const formatCurrency = (value: number | null | undefined) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '0.00%';
    return `${value.toFixed(2)}%`;
  };


  const nextPhoto = (propertyId: number, photoCount: number) => {
    setCurrentPhotoIndexes(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) + 1) % photoCount
    }));
  };

  const prevPhoto = (propertyId: number, photoCount: number) => {
    setCurrentPhotoIndexes(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) - 1 + photoCount) % photoCount
    }));
  };

  const getCurrentPhotoIndex = (propertyId: number) => {
    return currentPhotoIndexes[propertyId] || 0;
  };

  const updateProperty = (propertyId: number, updates: Partial<Property>) => {
    setBaseProperties(prev => 
      prev.map(p => p.id === propertyId ? { ...p, ...updates } : p)
    );
  };

  const handleRenovationsBlur = (propertyId: number, inputValue: string) => {
    const val = parseFloat(inputValue);
    if (isNaN(val) || val < 0) {
      setRenovationsInputs(prev => { const next = { ...prev }; delete next[propertyId]; return next; });
      updateProperty(propertyId, { renovations: 0 });
      return;
    }
    updateProperty(propertyId, { renovations: val });
  };

  const handlePurchasePriceBlur = (propertyId: number, inputValue: string, property: Property) => {
    const val = parseFloat(inputValue);
    const listPrice = property.list_price || 0;
    if (isNaN(val) || val <= 0) {
      setPurchasePriceInputs(prev => { const next = { ...prev }; delete next[propertyId]; return next; });
      updateProperty(propertyId, { purchase_price: listPrice });  // Reset to list price
      return;
    }
    updateProperty(propertyId, { purchase_price: val });
  };

  const handleToggleSection = (propertyId: number, section: string) => {
    setExpandedSectionsMap(prev => {
      const current = new Set(prev[propertyId] || []);
      if (current.has(section)) current.delete(section); else current.add(section);
      return { ...prev, [propertyId]: Array.from(current) };
    });
  };

  // Reset to page 1 when properties or sort order changes
  const prevSortRef = useRef<string>(sortBy);
  const prevPropsLenRef = useRef<number>(0);
  if (prevSortRef.current !== sortBy || prevPropsLenRef.current !== properties.length) {
    prevSortRef.current = sortBy;
    prevPropsLenRef.current = properties.length;
    if (currentPage !== 1) setCurrentPage(1);
  }

  // Sort properties based on selected sort option — STABLE: only recomputes via sortedPropertyIds useMemo
  const propMap = new Map(properties.map(p => [p.id, p]));
  const totalPages = Math.ceil(sortedPropertyIds.length / PAGE_SIZE);
  const pagedIds = sortedPropertyIds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pagedProperties = pagedIds.map(id => propMap.get(id)).filter(Boolean) as Property[];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="p-4">
        {/* ZIP Code Search */}
        <form onSubmit={handleZipSearch} className="mb-4">
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={zipInput}
                  onChange={(e) => setZipInput(e.target.value)}
                  placeholder="Search by ZIP code (e.g. 44105)"
                  maxLength={5}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Search
                </button>
                {activeZip && (
                  <button
                    type="button"
                    onClick={clearZipSearch}
                    className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Clear
                  </button>
                )}
              </div>
              {zipError && <p className="mt-1 text-xs text-red-600">{zipError}</p>}
              {activeZip && (
                <p className="mt-1 text-xs text-blue-600 font-medium">Showing ZIP {activeZip}</p>
              )}
            </div>
          </div>
        </form>

        {/* Empty state */}
        {properties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-gray-600 text-sm mb-2">No properties found matching your filters.</p>
            <p className="text-gray-500 text-xs">Try adjusting your filters. Data updates automatically every night from covered cities.</p>
          </div>
        ) : (
          <>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Properties ({properties.length.toLocaleString()})
              {lastFetchTime && (
                <span className="text-xs text-gray-500 font-normal ml-2">
                  — as of {lastFetchTime.toLocaleTimeString('en-US', { 
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </span>
              )}
            </h2>
            {lastSyncRun && (
              <p className="text-xs text-gray-500 mt-1">
                Last synced from RapidAPI: {new Date(lastSyncRun).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
          
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="cash_flow_desc">Cash Flow (High to Low)</option>
              <option value="cash_flow_asc">Cash Flow (Low to High)</option>
              <option value="price_asc">Price (Low to High)</option>
              <option value="price_desc">Price (High to Low)</option>
              <option value="cap_rate_desc">Cap Rate (High to Low)</option>
              <option value="cap_rate_asc">Cap Rate (Low to High)</option>
            </select>
          </div>
        </div>

        <div className="space-y-3">
          {pagedProperties.map((property) => {
            return (
              <div
                key={property.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex">
                  {/* Photo Column */}
                  {(property.photos?.length || property.photo_url) ? (
                    <div className="w-[420px] flex-shrink-0 flex flex-col rounded-l-lg overflow-hidden">

                      {/* Photo area */}
                      {property.photos && property.photos.length > 0 ? (
                        <div className="h-72 relative bg-gray-100 overflow-hidden">
                          <img 
                            src={property.photos[getCurrentPhotoIndex(property.id)]} 
                            alt={`${property.address} - ${getCurrentPhotoIndex(property.id) + 1}`}
                            className="w-full h-full object-contain bg-gray-100"
                            style={{ imageRendering: 'auto' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="384" height="288"%3E%3Crect width="384" height="288" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          {property.photos.length > 1 && (
                            <>
                              {/* Previous Button */}
                              <button
                                onClick={() => prevPhoto(property.id, property.photos!.length)}
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                                aria-label="Previous photo"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>

                              {/* Next Button */}
                              <button
                                onClick={() => nextPhoto(property.id, property.photos!.length)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                                aria-label="Next photo"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>

                              {/* Photo Counter */}
                              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                {getCurrentPhotoIndex(property.id) + 1} / {property.photos.length}
                              </div>

                              {/* Dot Indicators */}
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                                {property.photos.map((_, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => setCurrentPhotoIndexes(prev => ({ ...prev, [property.id]: idx }))}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                      idx === getCurrentPhotoIndex(property.id) 
                                        ? 'bg-white' 
                                        : 'bg-white/50 hover:bg-white/75'
                                    }`}
                                    aria-label={`Go to photo ${idx + 1}`}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="h-48 bg-gray-100 overflow-hidden">
                          <img 
                            src={property.photo_url!} 
                            alt={property.address}
                            className="w-full h-full object-contain bg-gray-100"
                            style={{ imageRendering: 'auto' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      {/* Links strip */}
                      {(() => {
                        const q = encodeURIComponent(`${property.address} ${property.city} ${property.state} ${property.zip_code}`);
                        return (
                          <div className="bg-white border-t border-gray-100 px-2 py-1.5 flex gap-1.5">
                            <a
                              href={`https://www.zillow.com/homes/${q}_rb/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded px-2 py-1 transition-colors"
                              onClick={e => e.stopPropagation()}
                              title="View on Zillow"
                            >
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              Zillow
                            </a>
                            <a
                              href={`https://maps.google.com/?q=${q}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded px-2 py-1 transition-colors"
                              onClick={e => e.stopPropagation()}
                              title="View on Google Maps"
                            >
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                              Maps
                            </a>
                          </div>
                        );
                      })()}

                    </div>
                  ) : null}
                  
                  {/* Property Details */}
                  <div className="flex-1 p-3 min-w-0">

                    {/* Header: Address + Live Cash Flow Badge */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0 pr-3">
                        <h3 className="text-base font-bold text-gray-900 leading-tight">
                          {property.address}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>{property.city}, {property.state} {property.zip_code}</span>
                          {property.property_type && (
                            <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded capitalize">
                              {property.property_type.replace(/_/g, ' ')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className={`flex-shrink-0 px-2.5 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 ${
                        (property.monthly_cash_flow || 0) >= 500 ? 'bg-green-100 text-green-800 border-green-300' :
                        (property.monthly_cash_flow || 0) >= 300 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' :
                        (property.monthly_cash_flow || 0) >= 0 ? 'bg-orange-50 text-orange-700 border-orange-300' :
                        'bg-red-50 text-red-700 border-red-300'
                      }`}>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          (property.monthly_cash_flow || 0) >= 300 ? 'bg-green-500' :
                          (property.monthly_cash_flow || 0) >= 0 ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}></span>
                        {formatCurrency(property.monthly_cash_flow)}/mo
                      </div>
                    </div>

                    {/* Property Quick Stats — compact horizontal strip */}
                    <div className="flex flex-wrap items-start gap-x-2.5 gap-y-1 pb-2 mb-2 border-b border-gray-100">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide leading-none mb-0.5">Purchase Price:</span>
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-md border-2 border-blue-300 hover:border-blue-400 focus-within:border-blue-500 bg-blue-50 transition-colors">
                          <span className="text-base font-bold text-blue-700 leading-none">$</span>
                          <input
                            type="number"
                            value={purchasePriceInputs[property.id] ?? (property.purchase_price ?? property.list_price)?.toString() ?? '0'}
                            onChange={(e) => setPurchasePriceInputs(prev => ({ ...prev, [property.id]: e.target.value }))}
                            onBlur={(e) => handlePurchasePriceBlur(property.id, e.target.value, property)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                              if (e.key === 'Escape') {
                                setPurchasePriceInputs(prev => { const next = { ...prev }; delete next[property.id]; return next; });
                                e.currentTarget.blur();
                              }
                            }}
                            className="text-base font-bold text-blue-700 bg-transparent focus:outline-none w-20 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            step="1000"
                            min="0"
                            title="Purchase/offer price — adjust to model negotiated price"
                          />
                        </div>
                        <span className="text-[9px] text-gray-400 leading-none">Listed: {formatCurrency(property.list_price)}</span>
                      </div>
                      <span className="text-gray-300 text-sm mt-5">+</span>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide leading-none mb-0.5">Rehab Cost:</span>
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-md border-2 border-orange-300 hover:border-orange-400 focus-within:border-orange-500 bg-orange-50 transition-colors">
                          <span className="text-base font-bold text-orange-700 leading-none">$</span>
                          <input
                            type="number"
                            value={renovationsInputs[property.id] ?? (property.renovations?.toString() ?? '0')}
                            onChange={(e) => setRenovationsInputs(prev => ({ ...prev, [property.id]: e.target.value }))}
                            onBlur={(e) => handleRenovationsBlur(property.id, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                              if (e.key === 'Escape') {
                                setRenovationsInputs(prev => { const next = { ...prev }; delete next[property.id]; return next; });
                                e.currentTarget.blur();
                              }
                            }}
                            className="text-base font-bold text-orange-700 bg-transparent focus:outline-none w-20 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            step="1000"
                            min="0"
                            title="Estimated renovation costs — adjust to see impact on returns"
                          />
                        </div>
                      </div>
                      <span className="text-gray-300 text-sm mt-5">=</span>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-green-600 font-semibold uppercase tracking-wide leading-none mb-0.5">Total Investment:</span>
                        <span className="text-lg font-black text-green-600 leading-none">{formatCurrency((property.purchase_price ?? property.list_price ?? 0) + (property.renovations || 0))}</span>
                      </div>
                      <span className="text-gray-300 text-sm">·</span>
                      <span className="text-sm text-gray-600">
                        <strong className="text-gray-900">{property.bedrooms || '—'}</strong> bd /
                        <strong className="text-gray-900"> {property.bathrooms || '—'}</strong> ba
                      </span>
                      <span className="text-gray-300 text-sm">·</span>
                      <span className="text-sm text-gray-600">
                        <strong className="text-gray-900">{property.sqft?.toLocaleString() || '—'}</strong> sqft
                      </span>
                      <span className="text-gray-300 text-sm">·</span>
                      <span className="text-sm text-gray-600">Built <strong className="text-gray-900">{property.year_built || 'Unknown'}</strong></span>
                    </div>

                    {/* ── INVESTMENT METRICS GRID ── */}
                    {(() => {
                      const cf = property.monthly_cash_flow || 0;
                      const arv = property.median_estimate || property.arv;
                      const price = property.list_price;
                      const equityGap = arv && price ? arv - price : null;
                      const equityPct = equityGap !== null && price ? (equityGap / price) * 100 : null;

                      // Shared card style helper
                      const card = (
                        gradient: string,
                        label: string,
                        tooltip: React.ReactNode,
                        value: React.ReactNode,
                        badge?: React.ReactNode
                      ) => (
                        <div className={`rounded-lg p-2.5 shadow-sm text-white flex flex-col ${gradient}`}>
                          <div className="flex items-start justify-between gap-1 mb-1.5">
                            <span className="text-[11px] font-bold opacity-85 uppercase tracking-wide leading-tight">{label}</span>
                            <div className="relative group flex-shrink-0">
                              <span className="text-white/50 cursor-help text-[11px]">ⓘ</span>
                              <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden group-hover:block z-50 w-64 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl">
                                {tooltip}
                                <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-900" />
                              </div>
                            </div>
                          </div>
                          <div className="text-xl font-black leading-tight flex-grow flex items-center justify-center">{value}</div>
                          {badge && <div className="text-[11px] opacity-85 mt-1.5 font-semibold">{badge}</div>}
                        </div>
                      );

                      const rent = property.section8_rent || 0;
                      const mortgage = property.monthly_mortgage || 0;
                      const expTotal = property.expense_breakdown?.total || 0;
                      const eb = property.expense_breakdown;
                      const propertyTax = eb?.property_tax || 0;
                      const insurance = eb?.insurance || 0;
                      const pmi = eb?.pmi || 0;
                      const totalHousingPayment = mortgage + propertyTax + insurance + pmi;
                      const annualRent = rent * 12;
                      const annualNoi = property.noi || 0;
                      const annualExpenses = annualRent - annualNoi;
                      const capRateArv = parseFloat(purchasePriceInputs[property.id] ?? '') || property.list_price || 0;
                      const cocReturn = property.cash_on_cash_return || 0;
                      const annualCF = cf * 12;
                      const totalCashInvested = cocReturn > 0 ? (annualCF / (cocReturn / 100)) : null;
                      const annualDebtService = mortgage * 12;
                      const dscr = annualDebtService > 0 ? (annualNoi / annualDebtService) : 0;
                      const onePercentRule = capRateArv > 0 ? ((rent / capRateArv) * 100) : 0;

                      return (
                        <div>
                          <div className="text-[10px] text-gray-400 italic mb-1.5">* Assumes full occupancy using current rent assumptions. Adjust rehab cost and rent assumptions as needed.</div>
                        <div className="grid grid-cols-5 gap-2 mb-2">
                          {/* Gross Monthly Rent */}
                          {card(
                            rent >= 1500 ? 'bg-gradient-to-br from-green-600 to-emerald-700' :
                            rent >= 1000 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                            rent > 0     ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
                                           'bg-gradient-to-br from-slate-400 to-slate-600',
                            'Gross Rent/mo',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Estimated Rent — Full Occupancy</div>
                              <div className="font-mono text-green-300 text-sm">{formatCurrency(rent)}/mo</div>
                              <div className="opacity-75">Assumes all units are fully occupied at the modeled rent for ZIP {property.zip_code} · {property.bedrooms ?? '?'}bd. Uses HUD FMR as a benchmark before expenses.</div>
                            </div>,
                            formatCurrency(property.section8_rent),
                            rent >= 1500 ? '🔥 Excellent' : rent >= 1000 ? '✅ Good' : rent > 0 ? '👍 Fair' : '—'
                          )}
                          {/* PITI Payment (Principal, Interest, Taxes, Insurance) */}
                          {card(
                            totalHousingPayment > 0
                              ? 'bg-gradient-to-br from-rose-500 to-pink-700'
                              : 'bg-gradient-to-br from-slate-400 to-slate-600',
                            'PITI Payment',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Principal + Interest + Taxes + Insurance + PMI</div>
                              <div className="font-mono text-pink-200 text-sm space-y-0.5">
                                <div>P&I (Mortgage): <span className="text-white">{formatCurrency(mortgage)}</span></div>
                                <div>Property Tax: <span className="text-white">{formatCurrency(propertyTax)}</span></div>
                                <div>Insurance (HOI): <span className="text-white">{formatCurrency(insurance)}</span></div>
                                {pmi > 0 && <div>PMI/MIP: <span className="text-white">{formatCurrency(pmi)}</span></div>}
                                <div className="pt-1 border-t border-white/30">Total PITI: <span className="text-white font-bold">{formatCurrency(totalHousingPayment)}/mo</span></div>
                              </div>
                              <div className="opacity-75">Your complete monthly payment to lender and escrow. Does not include maintenance, vacancy, management, or other operating expenses.</div>
                            </div>,
                            totalHousingPayment > 0 ? formatCurrency(totalHousingPayment) : '—',
                            totalHousingPayment > 0 ? `${formatCurrency(totalHousingPayment * 12)}/yr` : undefined
                          )}
                          {/* Total Monthly Expenses */}
                          {card(
                            'bg-gradient-to-br from-orange-500 to-red-700',
                            'All Monthly Costs',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Mortgage + All Operating Expenses</div>
                              {eb && (
                                <div className="font-mono text-orange-100 text-xs space-y-0.5">
                                  <div>Mortgage (P&I): <span className="text-white">{formatCurrency(mortgage)}</span></div>
                                  <div>Property Tax: <span className="text-white">{formatCurrency(eb.property_tax)}</span></div>
                                  <div>Insurance (HOI): <span className="text-white">{formatCurrency(eb.insurance)}</span></div>
                                  <div>Vacancy Reserve: <span className="text-white">{formatCurrency(eb.vacancy)}</span></div>
                                  <div>Maintenance: <span className="text-white">{formatCurrency(eb.maintenance)}</span></div>
                                  <div>Property Mgmt: <span className="text-white">{formatCurrency(eb.property_mgmt)}</span></div>
                                  <div>CapEx Reserve: <span className="text-white">{formatCurrency(eb.capex_reserve)}</span></div>
                                  {(eb.pmi ?? 0) > 0 && <div>PMI/MIP: <span className="text-white">{formatCurrency(eb.pmi)}</span></div>}
                                  <div className="pt-1 border-t border-white/30 mt-1">Total: <span className="text-white font-bold">{formatCurrency(mortgage + expTotal)}/mo</span></div>
                                </div>
                              )}
                              <div className="opacity-75 pt-1">Every dollar that goes out each month - mortgage payment plus all operating costs.</div>
                            </div>,
                            formatCurrency(mortgage + expTotal),
                            `${formatCurrency((mortgage + expTotal) * 12)}/year`
                          )}
                          {/* Cash Flow/mo */}
                          {card(
                            cf >= 500 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                            cf >= 300 ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
                            cf >= 0   ? 'bg-gradient-to-br from-orange-400 to-amber-500' :
                                        'bg-gradient-to-br from-red-500 to-rose-600',
                            'Cash Flow',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Rent − All Monthly Costs</div>
                              <div className="font-mono text-yellow-200 text-sm">
                                {formatCurrency(rent)} − {formatCurrency(mortgage + expTotal)} = <span className={cf >= 0 ? 'text-green-300' : 'text-red-300'}>{formatCurrency(cf)}/mo</span>
                              </div>
                              {eb && <div className="opacity-75 leading-relaxed">
                                Total Costs: Mortgage {formatCurrency(mortgage)} + Expenses (Tax, Insurance, Vacancy, Maint., Mgmt., CapEx{(eb.pmi ?? 0) > 0 ? ', PMI' : ''}) {formatCurrency(expTotal)}
                              </div>}
                            </div>,
                            formatCurrency(cf),
                            cf >= 500 ? '🔥 Excellent' : cf >= 300 ? '✅ Great' : cf >= 100 ? '👍 Good' : cf >= 0 ? '⚠️ Break-even' : '❌ Negative'
                          )}
                          {/* NOI */}
                          {card(
                            property.noi !== undefined && property.noi !== null && (property.noi || 0) > 0
                              ? 'bg-gradient-to-br from-cyan-500 to-teal-700'
                              : 'bg-gradient-to-br from-slate-400 to-slate-600',
                            'NOI (Annual)',
                            <div className="space-y-1">
                              <div className="font-bold text-white">(Rent × 12) − Annual Expenses</div>
                              <div className="font-mono text-cyan-200 text-sm">
                                ({formatCurrency(rent)} × 12) − {formatCurrency(annualExpenses)} = <span className="text-green-300">{formatCurrency(annualNoi)}</span>
                              </div>
                              <div className="opacity-75">Operating income excluding mortgage. Used for cap rate calculation.</div>
                            </div>,
                            property.noi !== undefined && property.noi !== null ? `${formatCurrency(property.noi)}/year` : '—',
                            undefined
                          )}
                          {/* Cap Rate */}
                          {card(
                            (property.cap_rate || 0) >= 10 ? 'bg-gradient-to-br from-blue-600 to-blue-800' :
                            (property.cap_rate || 0) >= 8  ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                            (property.cap_rate || 0) >= 5  ? 'bg-gradient-to-br from-sky-400 to-blue-600' :
                                                              'bg-gradient-to-br from-slate-400 to-slate-600',
                            'Cap Rate',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Annual NOI ÷ Purchase Price × 100</div>
                              <div className="font-mono text-blue-200 text-sm">
                                {formatCurrency(annualNoi)} ÷ {formatCurrency(capRateArv)} × 100 = <span className="text-green-300">{formatPercent(property.cap_rate)}</span>
                              </div>
                              <div className="opacity-75">Unleveraged return ignoring financing. 8%+ is often considered strong in many rental markets.</div>
                            </div>,
                            formatPercent(property.cap_rate),
                            (property.cap_rate || 0) >= 10 ? '🔥 Excellent' : (property.cap_rate || 0) >= 8 ? '✅ Strong' : (property.cap_rate || 0) >= 5 ? '⚠️ Fair' : '📉 Weak'
                          )}
                          {/* Cash-on-Cash */}
                          {card(
                            (property.cash_on_cash_return || 0) >= 20 ? 'bg-gradient-to-br from-violet-600 to-purple-800' :
                            (property.cash_on_cash_return || 0) >= 15 ? 'bg-gradient-to-br from-violet-500 to-purple-700' :
                            (property.cash_on_cash_return || 0) >= 10 ? 'bg-gradient-to-br from-purple-400 to-violet-600' :
                            (property.cash_on_cash_return || 0) > 0   ? 'bg-gradient-to-br from-slate-400 to-slate-600' :
                                                         'bg-gradient-to-br from-red-500 to-rose-600',
                            'Cash-on-Cash Return',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Annual Cash Flow ÷ Cash Invested × 100</div>
                              <div className="font-mono text-purple-200 text-sm">
                                {formatCurrency(annualCF)} ÷ {totalCashInvested ? formatCurrency(totalCashInvested) : '?'} × 100 = <span className="text-green-300">{property.cash_on_cash_return != null ? formatPercent(property.cash_on_cash_return) : 'N/A'}</span>
                              </div>
                              <div className="opacity-75">Cash invested = down payment + closing costs + rehab. Measures return on actual dollars out of pocket.</div>
                              <div className="opacity-75 pt-1 border-t border-white/20">
                                <strong>Payback</strong> = years until cumulative cash flow returns your cash invested (100 ÷ CoC return).
                                {property.breakeven_years != null && property.cash_on_cash_return != null && (
                                  <span className="block font-mono text-purple-200 mt-0.5">100 ÷ {property.cash_on_cash_return.toFixed(1)}% = {property.breakeven_years.toFixed(1)} yrs</span>
                                )}
                              </div>
                            </div>,
                            property.cash_on_cash_return !== undefined && property.cash_on_cash_return !== null ? formatPercent(property.cash_on_cash_return) : 'N/A',
                            property.breakeven_years !== undefined && property.breakeven_years !== null
                              ? `Payback: ${property.breakeven_years.toFixed(1)} yrs`
                              : ((property.cash_on_cash_return || 0) >= 20 ? '🔥 Excellent' : (property.cash_on_cash_return || 0) >= 15 ? '✅ Great' : (property.cash_on_cash_return || 0) >= 10 ? '👍 Good' : (property.cash_on_cash_return || 0) > 0 ? '⚠️ Low' : '❌ Negative')
                          )}
                          {/* DSCR */}
                          {card(
                            dscr >= 1.25 ? 'bg-gradient-to-br from-indigo-500 to-blue-700' :
                            dscr >= 1.0  ? 'bg-gradient-to-br from-yellow-500 to-orange-600' :
                                           'bg-gradient-to-br from-red-500 to-rose-700',
                            'DSCR',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Debt Service Coverage Ratio</div>
                              <div className="font-mono text-indigo-200 text-sm">
                                NOI ÷ Annual Debt Service = <span className="text-green-300">{dscr.toFixed(2)}x</span>
                              </div>
                              <div className="font-mono text-xs">
                                {formatCurrency(annualNoi)} ÷ {formatCurrency(annualDebtService)} = {dscr.toFixed(2)}x
                              </div>
                              <div className="opacity-75">How many times NOI covers mortgage payments. Lenders typically require 1.25x minimum. Higher is safer.</div>
                            </div>,
                            `${dscr.toFixed(2)}x`,
                            dscr >= 1.25 ? '✅ Strong' : dscr >= 1.0 ? '⚠️ Marginal' : '❌ Weak'
                          )}
                          {/* 1% Rule */}
                          {card(
                            onePercentRule >= 1.0 ? 'bg-gradient-to-br from-emerald-500 to-green-700' :
                            onePercentRule >= 0.8 ? 'bg-gradient-to-br from-yellow-500 to-orange-600' :
                                                     'bg-gradient-to-br from-red-500 to-rose-700',
                            '1% Rule',
                            <div className="space-y-1">
                              <div className="font-bold text-white">Monthly Rent ÷ Purchase Price × 100</div>
                              <div className="font-mono text-emerald-200 text-sm">
                                {formatCurrency(rent)} ÷ {formatCurrency(capRateArv)} × 100 = <span className="text-green-300">{onePercentRule.toFixed(2)}%</span>
                              </div>
                              <div className="opacity-75">Quick screening metric. 1%+ means monthly rent is 1% or more of purchase price. Higher is better for cash flow.</div>
                            </div>,
                            `${onePercentRule.toFixed(2)}%`,
                            onePercentRule >= 1.0 ? '🔥 Meets 1% Rule' : onePercentRule >= 0.8 ? '⚠️ Close' : '❌ Below 1%'
                          )}
                          {/* Break-Even Price */}
                          {property.break_even_price && property.list_price
                            ? (() => {
                                const bep = property.break_even_price;
                                const lp = parseFloat(purchasePriceInputs[property.id] ?? '') || property.list_price;
                                const under = lp <= bep;
                                const diff = under ? bep - lp : lp - bep;
                                return card(
                                  under
                                    ? 'bg-gradient-to-br from-teal-500 to-cyan-700'
                                    : 'bg-gradient-to-br from-red-500 to-rose-700',
                                  'Max Buy Price',
                                  <div className="space-y-1">
                                    <div className="font-bold text-white">Break-even Purchase Price</div>
                                    <div className="font-mono text-teal-200 text-sm">Max price for $0/mo cash flow = <span className="text-green-300">{formatCurrency(bep)}</span></div>
                                    <div className="font-mono text-sm">{purchasePriceInputs[property.id] ? 'Your price' : 'List price'} {formatCurrency(lp)} is <span className={under ? 'text-green-300' : 'text-red-300'}>{formatCurrency(diff)} {under ? 'under ✅' : 'over ⚠️'}</span> this limit</div>
                                    <div className="opacity-75">Calculated by finding the price where: Rent = Mortgage + All Expenses.</div>
                                  </div>,
                                  formatCurrency(bep),
                                  under
                                    ? `✅ ${formatCurrency(diff)} under`
                                    : `⚠️ ${formatCurrency(diff)} over`
                                );
                              })()
                            : card('bg-gradient-to-br from-slate-400 to-slate-600', 'Max Buy Price', 'Break-even purchase price not yet calculated.', '—')
                          }
                        </div>
                        </div>
                      );
                    })()}

                    {/* Analysis Sections - Collapsible Accordion */}
                    <AnalysisAccordion
                      property={property}
                      onPropertyUpdate={updateProperty}
                      expandedSections={expandedSectionsMap[property.id] || []}
                      onToggleSection={(section) => handleToggleSection(property.id, section)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, sortedPropertyIds.length)} of {sortedPropertyIds.length} properties
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="First page"
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ‹ Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === 'ellipsis' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-gray-400">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p as number)}
                        className={`px-3 py-1.5 text-sm rounded border ${
                          currentPage === p
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )
                }
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Last page"
                >
                  »
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default PropertyList;
